import { SignUpForm, SignUpFormTheme, SignUpFormField } from '@masonic-tools/shared-ui';
import { useAuth } from '@/contexts/AuthContext';
import { validateEmail, validatePassword } from '@/lib/utils';
import { showError } from '@/lib/swal';
import Header from '@/components/layout/Header';

const diningTheme: SignUpFormTheme = {
  logo: '/logo.png',
  appName: 'Dining Steward',
  appNameColor: 'text-navy',
  title: 'Create Your Account',
  description: 'Join the modern way to manage Masonic dining events. Free to use, forever.',
  loginPath: '/login',
  termsPath: '/terms',
  privacyPath: '/privacy',
  successRedirectPath: '/verify-email-pending',
};

export default function SignUpPage() {
  const { signup } = useAuth();

  const signUpFields: SignUpFormField[] = [
    {
      name: 'firstName',
      label: 'First Name',
      type: 'text',
      placeholder: 'John',
      required: true,
    },
    {
      name: 'lastName',
      label: 'Last Name',
      type: 'text',
      placeholder: 'Smith',
      required: true,
    },
    {
      name: 'email',
      label: 'Email Address',
      type: 'email',
      placeholder: 'john@example.com',
      required: true,
    },
    {
      name: 'password',
      label: 'Password',
      type: 'password',
      placeholder: '••••••••',
      required: true,
      helpText: 'At least 8 characters, 1 uppercase letter, and 1 number',
    },
    {
      name: 'confirmPassword',
      label: 'Confirm Password',
      type: 'password',
      placeholder: '••••••••',
      required: true,
    },
  ];

  const validateForm = (data: Record<string, any>) => {
    // Validation
    if (!data.firstName?.trim()) {
      return { isValid: false, error: 'Please enter your first name' };
    }

    if (!data.lastName?.trim()) {
      return { isValid: false, error: 'Please enter your last name' };
    }

    if (!validateEmail(data.email)) {
      return { isValid: false, error: 'Please enter a valid email address' };
    }

    const passwordValidation = validatePassword(data.password);
    if (!passwordValidation.isValid) {
      return { isValid: false, error: passwordValidation.errors.join('. ') };
    }

    if (data.password !== data.confirmPassword) {
      return { isValid: false, error: 'Passwords do not match' };
    }

    return { isValid: true };
  };

  const handleSignUp = async (data: Record<string, any>) => {
    const { email, password, confirmPassword, ...additionalData } = data;
    await signup(email, password, additionalData);
  };

  return (
    <SignUpForm
      theme={diningTheme}
      fields={signUpFields}
      onSignUp={handleSignUp}
      onError={showError}
      header={<Header />}
      validateForm={validateForm}
      additionalContent={
        <div className="mt-4 rounded-md bg-yellow-50 dark:bg-yellow-900/20 p-3 text-sm text-yellow-800 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-800">
          <p className="font-medium mb-1">Early Access Notice</p>
          <p className="text-xs">
            This system is currently in early access. You may encounter issues or unexpected behavior. 
            We appreciate your patience as we continue to improve the platform.
          </p>
        </div>
      }
    />
  );
}
